import { styled } from '@linaria/react';
import {
  inkDarkest,
  inkLight,
  inkLighter,
  primaryDarkest,
  redDarkest,
  secondaryBase,
  skyLight,
  skyLightest,
} from '../../../../styles/design-tokens';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  position: relative;
`;

const Body = styled.div`
  padding: 4px 0 16px;
  display: flex;
  flex-direction: column;
`;

const PrMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
`;

const Repo = styled.span`
  font: 500 10.5px/1.3 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Branches = styled.div`
  font: 500 10.5px/1.3 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  font: 500 11.5px/1.3 'Figtree', sans-serif;
  color: ${secondaryBase};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const ReplyChip = styled.span`
  padding: 0 6px;
  border-radius: 99px;
  border: 1px solid ${skyLight};
  font: 500 10px/1.5 'Figtree', sans-serif;
  color: ${inkLight};
`;

const CodeContext = styled.details`
  margin-top: 4px;
  font: 500 11px/1.3 'Figtree', sans-serif;
  color: ${inkLight};

  summary {
    cursor: pointer;
  }

  pre {
    margin: 6px 0 0;
    padding: 8px;
    max-height: 200px;
    overflow: auto;
    background: white;
    border: 1px solid ${skyLight};
    border-radius: 6px;
    font: 500 10.5px/1.4 'SF Mono', ui-monospace, Menlo, monospace;
    color: ${inkDarkest};
    white-space: pre;
  }
`;

const DetailsNote = styled.div`
  font: 400 11px/1.4 'Figtree', sans-serif;
  font-style: italic;
  color: ${inkLighter};
`;

const StateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 8px;
  border-radius: 99px;
  font: 600 10px/1.4 'Figtree', sans-serif;
  text-transform: capitalize;
`;

const Added = styled.span`
  color: ${primaryDarkest};
  font-weight: 600;
`;

const Removed = styled.span`
  color: ${redDarkest};
  font-weight: 600;
`;

/** Matches a small ActionIcon (28px) so the counter stays put when an arrow is removed. */
const ArrowSpacer = styled.span`
  display: inline-block;
  width: 28px;
  height: 28px;
`;

const Position = styled.span`
  min-width: 44px;
  text-align: center;
  font: 500 11px/1 'Figtree', sans-serif;
  color: ${inkLight};
  font-variant-numeric: tabular-nums;
`;

const CommentCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;
  padding: 10px 12px;
  background: ${skyLightest};
  border: 1px solid ${skyLight};
  border-radius: 8px;
`;

const FilePath = styled.div`
  font: 500 10.5px/1.3 'SF Mono', ui-monospace, Menlo, monospace;
  color: ${inkLight};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MarkdownBody = styled.div`
  font: 400 12.5px/1.5 'Figtree', sans-serif;
  color: ${inkDarkest};
  /* break-word, not anywhere: anywhere also shrinks a table's minimum width,
     which let every column collapse to a character or two. */
  overflow-wrap: break-word;
  min-width: 0;

  > :first-child {
    margin-top: 0;
  }
  > :last-child {
    margin-bottom: 0;
  }
  p {
    margin: 0 0 8px;
  }
  ul,
  ol {
    margin: 0 0 8px;
    padding-left: 18px;
  }
  h1,
  h2,
  h3,
  h4 {
    font-size: 13px;
    font-weight: 600;
    margin: 8px 0 4px;
  }
  code {
    font: 500 11px/1.4 'SF Mono', ui-monospace, Menlo, monospace;
    background: white;
    border: 1px solid ${skyLight};
    border-radius: 4px;
    padding: 0 4px;
  }
  pre {
    margin: 0 0 8px;
    padding: 8px;
    background: white;
    border: 1px solid ${skyLight};
    border-radius: 6px;
    overflow-x: auto;
  }
  pre code {
    border: none;
    padding: 0;
  }
  blockquote {
    margin: 0 0 8px;
    padding-left: 8px;
    border-left: 3px solid ${skyLight};
    color: ${inkLight};
  }
  a {
    color: ${secondaryBase};
  }
  /* Wide tables scroll sideways inside the comment instead of squeezing
     their columns; cells keep a readable minimum width. */
  table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    margin: 0 0 8px;
  }
  th,
  td {
    min-width: 72px;
    border: 1px solid ${skyLight};
    padding: 3px 8px;
    vertical-align: top;
    text-align: left;
  }
  th {
    background: white;
    font-weight: 600;
    white-space: nowrap;
  }
`;

const EmptyBody = styled.div`
  font: 400 12px/1.4 'Figtree', sans-serif;
  font-style: italic;
  color: ${inkLighter};
`;

export {
  Added,
  ArrowSpacer,
  Body,
  Branches,
  CodeContext,
  CommentCard,
  DetailsNote,
  EmptyBody,
  ExternalLink,
  FilePath,
  MarkdownBody,
  Position,
  PrMeta,
  Removed,
  ReplyChip,
  Repo,
  StateBadge,
  Wrapper,
};
